import { Box } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { PieChartProps } from '@mui/x-charts';
import React, { useMemo } from 'react';

import PieChart from 'components/charts/pie-chart';
import ChartLegend from 'components/charts/chart-legend';
import { PIE_EMPHASIS } from 'components/charts/constants/chart-defaults';
import { useChartLegendVisibility } from 'components/charts/hooks/use-chart-legend-visibility';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useSeriesVisibility } from 'components/charts/hooks/use-series-visibility';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLabChartHiddenSeriesOverlay from 'views/playground/features/data-lab/components/data-lab-chart-hidden-series-overlay';
import DataLabChartLegendVisibilityMenu from 'views/playground/features/data-lab/components/data-lab-chart-legend-visibility-menu';
import DataLabChartTooltip from 'views/playground/features/data-lab/components/data-lab-chart-tooltip';
import { SelectableChartProps } from 'views/playground/features/data-lab/types/selectable-chart.type';
import { CategoryPoint, NestedCategoryData } from 'views/playground/features/data-lab/types/chart-series.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

type Props = SelectableChartProps &
  Pick<PieChartProps, 'height'> & {
    data: NestedCategoryData;
  };

type ColoredCategoryPoint = CategoryPoint & {
  color: string;
};

type NestedGroup = {
  parent: ColoredCategoryPoint;
  items: ColoredCategoryPoint[];
};

// Both rings are narrow, so small arc labels are suppressed independently.
const ARC_LABEL_MIN_ANGLE = 20;

const NestedDonutChart: React.FC<Props> = ({ data, height = 320, selectedId, onSelect }) => {
  const { formatKg } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const { visible: legendVisible, toggle: toggleLegend } = useChartLegendVisibility();

  const outerItems = useMemo(
    () => data.outer.map((point, index) => ({ ...point, color: palette.series.categorical[index % palette.series.categorical.length] })),
    [data.outer, palette.series.categorical]
  );

  const innerGroups = useMemo<NestedGroup[]>(() => {
    if (outerItems.length === 0) return [];

    // The flat gallery fixture orders child slices by outer group, so assign consecutive slices without changing the input type.
    const innerPerOuter = Math.ceil(data.inner.length / outerItems.length);

    return outerItems.map((parent, parentIndex) => {
      const children = data.inner.slice(parentIndex * innerPerOuter, (parentIndex + 1) * innerPerOuter);
      return {
        parent,
        items: children.map((point, index) => {
          const lightenAmount = 0.15 + (index / Math.max(children.length - 1, 1)) * 0.35;
          return { ...point, color: lighten(parent.color, lightenAmount) };
        }),
      };
    });
  }, [data.inner, outerItems]);

  const innerItems = useMemo<ColoredCategoryPoint[]>(
    () =>
      outerItems.length === 0
        ? data.inner.map((point, index) => ({
            ...point,
            color: palette.series.categorical[index % palette.series.categorical.length],
          }))
        : innerGroups.flatMap((group) => group.items),
    [data.inner, innerGroups, outerItems.length, palette.series.categorical]
  );

  const legendItems = useMemo(() => [...outerItems, ...innerItems], [outerItems, innerItems]);
  // Keep explicit toggles separate so hiding a parent does not overwrite individual child choices.
  const { hiddenIds, toggle } = useSeriesVisibility(legendItems);
  // Expand hidden parents for chart filtering and legend state; independently hidden children stay hidden when a parent is shown again.
  const effectiveHiddenIds = useMemo(
    () => [
      ...hiddenIds,
      ...innerGroups.flatMap((group) =>
        hiddenIds.includes(group.parent.id) ? group.items.filter((item) => !hiddenIds.includes(item.id)).map((item) => item.id) : []
      ),
    ],
    [hiddenIds, innerGroups]
  );
  const visibleOuter = useMemo(
    () =>
      data.inner.length === 0
        ? outerItems.filter((item) => !effectiveHiddenIds.includes(item.id))
        : innerGroups
            .filter((group) => !effectiveHiddenIds.includes(group.parent.id))
            .map((group) => ({
              ...group.parent,
              value: group.items.reduce((total, item) => (effectiveHiddenIds.includes(item.id) ? total : total + item.value), 0),
            })),
    [data.inner.length, effectiveHiddenIds, innerGroups, outerItems]
  );
  const visibleInner = innerItems.filter((item) => !effectiveHiddenIds.includes(item.id));

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <DataLabChartLegendVisibilityMenu visible={legendVisible} onToggle={toggleLegend} />
      </Box>
      <Box height={height}>
        <PieChart
          height={height}
          skipAnimation={chartSkipAnimation}
          hideLegend
          slots={{ noDataOverlay: DataLabChartHiddenSeriesOverlay, tooltip: DataLabChartTooltip }}
          series={[
            {
              id: 'outer',
              innerRadius: '68%',
              outerRadius: '88%',
              cornerRadius: 6,
              paddingAngle: 2,
              ...PIE_EMPHASIS,
              data: visibleOuter.map((point) => ({ id: point.id, label: point.label, value: point.value, color: point.color })),
              valueFormatter: (value) => formatKg(value.value),
              // The outer ring has two large slices, and its labels sat close
              // enough to the inner ring's own labels, radially, that a wide
              // formatted value collided with whichever inner label shared
              // roughly the same angle. The outer ring's two buckets are
              // already named in the legend below and are large enough to
              // read as "the top half" and "the bottom half" without a
              // number; the inner ring is where a value earns its place.
            },
            {
              id: 'inner',
              innerRadius: '42%',
              outerRadius: '64%',
              cornerRadius: 4,
              paddingAngle: 2,
              ...PIE_EMPHASIS,
              data: visibleInner.map((point) => ({ id: point.id, label: point.label, value: point.value, color: point.color })),
              valueFormatter: (value) => formatKg(value.value),
              // The legend carries the category name, so the arc label shows
              // the value.
              arcLabel: (item) => formatKg(item.value),
              arcLabelMinAngle: ARC_LABEL_MIN_ANGLE,
            },
          ]}
          onItemClick={(_event, pieItemIdentifier) => {
            const source = pieItemIdentifier.seriesId === 'outer' ? visibleOuter : visibleInner;
            const category = source[pieItemIdentifier.dataIndex];
            if (!category) return;
            onSelect?.(category.id === selectedId ? null : category.id);
          }}
        />
      </Box>
      {legendVisible &&
        (outerItems.length === 0 ? (
          <ChartLegend
            items={innerItems.map((item) => ({ id: item.id, label: item.label, color: item.color }))}
            hiddenIds={effectiveHiddenIds}
            onToggle={toggle}
          />
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {innerGroups.map(({ parent, items }) => (
              <Box key={parent.id} role="group" aria-label={parent.label}>
                <ChartLegend
                  items={[{ id: parent.id, label: parent.label, color: parent.color }]}
                  hiddenIds={effectiveHiddenIds}
                  onToggle={toggle}
                />
                {items.length > 0 && (
                  <Box paddingLeft={3}>
                    <ChartLegend
                      items={items.map((item) => ({ id: item.id, label: item.label, color: item.color }))}
                      hiddenIds={effectiveHiddenIds}
                      onToggle={toggle}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ))}
    </>
  );
};

export default NestedDonutChart;
